/**
 * Cover-art search (online) — proxied through the backend so we don't hit
 * CORS issues with MusicBrainz / Cover Art Archive directly from the
 * browser, and so we can swap providers later without touching the UI.
 *
 * The backend tries MusicBrainz → Cover Art Archive (no API key needed).
 * If anything is found, it streams the image bytes back with the proper
 * Content-Type. The browser then turns that into a Blob + URL the
 * NewMapDialog can preview.
 */

import { API_BASE } from '../../api/apiBase';

export interface FetchedCover {
  bytes: ArrayBuffer;
  mime: string;
  name: string;
}

/**
 * Look up cover art for an (artist, title) pair. Returns null when nothing
 * was found, the backend is unreachable, or the only candidates were
 * smaller than `minSize` × `minSize`.
 */
export async function searchCoverArt(
  artist: string,
  title: string,
  minSize: number = 1024,
): Promise<FetchedCover | null> {
  if (!artist && !title) return null;
  const url = `${API_BASE}/api/cover/search`
    + `?artist=${encodeURIComponent(artist)}`
    + `&title=${encodeURIComponent(title)}`
    + `&min_size=${minSize}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (blob.size === 0) return null;
    const bytes = await blob.arrayBuffer();
    const mime = blob.type || 'image/jpeg';
    const ext = mime.split('/').pop()?.split('+')[0] || 'jpg';
    return {
      bytes,
      mime,
      name: `cover.${ext}`,
    };
  } catch {
    return null;
  }
}
